import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Slide,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert
} from '@mui/material';
import {
  GetApp as InstallIcon,
  Close as CloseIcon,
  Smartphone as PhoneIcon,
  Computer as ComputerIcon
} from '@mui/icons-material';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    // Check if app is already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isRunningStandalone = window.navigator.standalone;
    
    if (isStandalone || isRunningStandalone) {
      return; // Don't show prompt if already installed
    }

    // Check if user has already dismissed the prompt
    const hasPromptBeenDismissed = localStorage.getItem('pwa-install-dismissed');
    const lastDismissed = localStorage.getItem('pwa-install-dismissed-time');
    
    // Show prompt again after 7 days
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    const shouldShowAgain = !lastDismissed || parseInt(lastDismissed) < sevenDaysAgo;

    if (hasPromptBeenDismissed && !shouldShowAgain) {
      return;
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      setIsInstallable(true);
      
      // Show our custom prompt after a short delay
      setTimeout(() => {
        setShowInstallPrompt(true);
      }, 3000); // Show after 3 seconds
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS Safari (doesn't support beforeinstallprompt)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isInStandaloneMode = window.navigator.standalone;
    
    if (isIOS && !isInStandaloneMode && shouldShowAgain) {
      setTimeout(() => {
        setShowInstallPrompt(true);
        setIsInstallable(true);
      }, 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      // For iOS Safari - show manual instructions
      setShowInstallPrompt(false);
      setShowSuccessMessage(true);
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
      setShowSuccessMessage(true);
    } else {
      console.log('User dismissed the install prompt');
    }
    
    // Clear the saved prompt since it can't be used again
    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
    localStorage.setItem('pwa-install-dismissed-time', Date.now().toString());
  };

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  if (!showInstallPrompt) return null;

  return (
    <>
      {/* Install Prompt */}
      <Slide direction="up" in={showInstallPrompt} timeout={500}>
        <Box
          sx={{
            position: 'fixed',
            bottom: { xs: 80, sm: 24 }, // Above mobile bottom navigation
            right: { xs: 16, sm: 24 },
            maxWidth: { xs: 280, sm: 320 },
            zIndex: 1300,
          }}
        >
          <Card 
            elevation={4}
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              backdropFilter: 'blur(10px)',
            }}
          >
            <CardContent sx={{ p: 2, pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 1, 
                  backgroundColor: 'primary.main',
                  mr: 1.5,
                  minWidth: 'auto'
                }}>
                  {isMobile ? 
                    <PhoneIcon sx={{ fontSize: 18, color: 'white' }} /> : 
                    <ComputerIcon sx={{ fontSize: 18, color: 'white' }} />
                  }
                </Box>
                
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      lineHeight: 1.3,
                      mb: 0.5
                    }}
                  >
                    تثبيت التطبيق
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      fontSize: '0.8rem',
                      lineHeight: 1.3
                    }}
                  >
                    وصول أسرع وتجربة محسّنة
                  </Typography>
                </Box>
                
                <IconButton
                  onClick={handleDismiss}
                  size="small"
                  sx={{ 
                    color: 'text.secondary',
                    ml: 0.5,
                    mt: -0.5
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </CardContent>

            <CardActions sx={{ p: 2, pt: 0 }}>
              <Button
                variant="contained"
                fullWidth
                size="small"
                onClick={handleInstallClick}
                startIcon={<InstallIcon />}
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  py: 0.75,
                  background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                  boxShadow: 1,
                  '& .MuiButton-startIcon': {
                    marginLeft: 1.5, // Add more space between icon and text (Arabic RTL)
                    marginRight: 1.25
                  },
                  '&:hover': {
                    background: 'linear-gradient(45deg, #1565c0, #1e88e5)',
                    boxShadow: 2
                  }
                }}
              >
                {isIOS ? 'إرشادات التثبيت' : 'تثبيت الآن'}
              </Button>
            </CardActions>
          </Card>
        </Box>
      </Slide>

      {/* Success Message */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={6000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ mb: { xs: 10, sm: 2 } }} // Above mobile navigation
      >
        <Alert
          onClose={() => setShowSuccessMessage(false)}
          severity="info"
          variant="filled"
          sx={{ 
            width: '100%',
            borderRadius: 2,
            fontSize: '0.85rem',
            '& .MuiAlert-icon': {
              fontSize: '1.1rem'
            }
          }}
        >
          {isIOS ? (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.85rem' }}>
                لتثبيت التطبيق على iOS:
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
                1. اضغط على أيقونة المشاركة في سفاري
                <br />
                2. اختر "إضافة إلى الشاشة الرئيسية"
                <br />
                3. اضغط "إضافة"
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
              تم بدء تثبيت التطبيق! ستجده قريباً في قائمة التطبيقات.
            </Typography>
          )}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PWAInstallPrompt;
